import 'babel-polyfill';

import uiRouter from '@uirouter/angularjs';
import 'libs/angular-material-custom/angular-material';
import 'libs/angular-material-custom/angular-material.css';
import ngAnimate from 'angular-animate';
import ngAria from 'angular-aria';
import ngMessages from 'angular-messages';
import ravenJs from 'raven-js';
import ngRaven from 'raven-js/plugins/angular';
import get from 'lodash/get';

import 'constants/constants.json';

import { FaviconDirective } from 'directives/favicon';
import { ErrorInterceptor } from 'services/errorInterceptor';
import { ApiService } from 'services/api.service';
import { CheckoutService } from 'services/checkout.service';
import { LaunchDarklyService } from 'services/launchDarkly.service';
import { GaService } from 'services/ga.service';
import { ScriptLoaderService } from 'services/scriptLoader.service';
import { StoreInfoService } from 'services/storeInfo.service';
import { ShipToLocationsService } from 'services/shipToLocations.service';
import { FullscreenLoaderService } from 'components/fullscreenLoader/fullscreenLoader.service';
import { getSavedCartInfo } from 'services/cartUtils';
import { ApiInterceptorModule } from 'services/api.interceptor';
import { ComponentRouter } from './componentRouter.component';

import '../styles/styles.scss';

ravenJs
    .config('https://2e5e6b9cb952427f958bc6c04fcab3ea@sentry.io/92936', {
        release: WEBPACK_GITHUB_VERSION,
        environment: WEBPACK_ENV,
        ignoreUrls: [
            /localhost:\d+\//i,
            /127\.0\.0\.1:\d+\//i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i
        ]
    })
    .addPlugin(ngRaven, angular)
    .install();

export const AppModule = angular
    .module('app', [
        uiRouter,
        'ngMaterial',

        ngAnimate,
        ngAria,
        ngMessages,
        ngRaven.moduleName,

        'app.constants',

        ErrorInterceptor,
        ApiService,
        CheckoutService,
        LaunchDarklyService,
        GaService,
        ScriptLoaderService,
        StoreInfoService,
        FullscreenLoaderService,
        FaviconDirective,
        ShipToLocationsService,
        ComponentRouter,
        ApiInterceptorModule
    ])
    .config(($httpProvider) => {
        $httpProvider.interceptors.push('apiInterceptor');
    })
    .config(($provide, $httpProvider) => {
        $provide.decorator('$exceptionHandler', ($delegate, Raven) => {
            return (exception, cause) => {
                Raven.captureException(exception, {
                    extra: cause
                });
                $delegate(exception, cause);
            };
        });

        $httpProvider.interceptors.push('errorInterceptor');
    })
    .config(($compileProvider) => {
        $compileProvider.commentDirectivesEnabled(false);
        $compileProvider.cssClassDirectivesEnabled(false);
        $compileProvider.debugInfoEnabled(false);
    })
    .config(($mdThemingProvider) => {
        $mdThemingProvider
            .definePalette('checkoutPrimary', {
                50: '#000',
                100: '#000',
                200: '#000',
                300: '#000',
                400: '#000',
                500: '#000',
                600: '#000',
                700: '#000',
                800: '#000',
                900: '#000',
                A100: '#000',
                A200: '#000',
                A400: '#000',
                A700: '#000',
                contrastDefaultColor: 'light',
                contrastDarkColors: ['50', '100', '200', '300', '400', 'A100'],
                contrastLightColors: undefined
            })
            .theme('checkout')
            .primaryPalette('checkoutPrimary')
            .accentPalette('grey');

        $mdThemingProvider.setDefaultTheme('checkout');
    })
    .config(($stateProvider, $locationProvider) => {
        $locationProvider.html5Mode(true);

        $stateProvider.state('root', {
            url: '',
            abstract: true,
            reloadOnSearch: false,
            template: '<ui-view></ui-view>',
            resolve: {
                bag: (
                    apiService,
                    $stateParams,
                    $window,
                    $q,
                    CART_LOCAL_STORAGE_KEY,
                    TENANT_LOCAL_STORAGE_KEY,
                    USE_NEW_CART_SERVICE_KEY
                ) => {
                    const localCartId = $window.sessionStorage.getItem(CART_LOCAL_STORAGE_KEY);
                    const localTenantId = $window.sessionStorage.getItem(TENANT_LOCAL_STORAGE_KEY);
                    const localUseNewCarts = $window.sessionStorage.getItem(USE_NEW_CART_SERVICE_KEY);

                    if (localTenantId && (localCartId || localUseNewCarts)) {
                        return $q.resolve({
                            tenantId: localTenantId,
                            cartId: localCartId,
                            useNewCarts: true
                        });
                    }
                    return apiService.getBag($stateParams.bag).catch(() => {
                        $window.location.href = $window.location.origin;
                    });
                },
                useNewCarts: (initLaunchDarkly, launchDarklyService, bag) =>
                    launchDarklyService.getFeature('use-new-carts-in-checkout', false) && bag.useNewCarts,
                tenantId: bag => bag.tenantId,
                setApiServiceTenant: (apiService, tenantId) => apiService.setTenantId(tenantId),
                setAuth0Client: (setApiServiceTenant, apiService, auth0SdkService, Raven) => {
                    return apiService.getClientId().then(clientId =>
                        auth0SdkService.setAuth0Client(clientId)).catch((err) => {
                        Raven.captureException(err);
                    });
                },
                getToken: (setAuth0Client, authService, Raven) => {
                    return authService.getAccessToken().then(token => token).catch((err) => {
                        Raven.captureException(err);
                    });
                },
                handleRedirectCallback: (setAuth0Client, $window, authService) => {
                    const params = (new URL($window.location)).searchParams;
                    const code = params.get('code');
                    const state = params.get('state');
                    const shouldParseResult = code && state;

                    if (shouldParseResult) {
                        const bag = (new URL($window.location)).searchParams.get('bag');
                        return authService.handleRedirectCallback().then(() => {
                            if (bag) {
                                $window.history.pushState({}, '', `/checkout/?bag=${bag}`);
                            } else {
                                $window.history.pushState({}, '', '/checkout/');
                            }
                        });
                    }
                },
                isLoggedIn: (getToken, setApiServiceTenant, handleRedirectCallback, authService) =>
                    authService.isLoggedIn().then(authenticated => authenticated),
                setLoadingHooks: (setApiServiceTenant, loadingService, paypalSdkService, $location,
                    $transitions, isLoggedIn) => {
                    if (!isLoggedIn && !paypalSdkService.hasPaypalQueryParams($location.search())) {
                        $transitions.onBefore({ to: 'checkout' }, () => loadingService.show());
                        $transitions.onSuccess({ to: 'checkout' }, () => loadingService.hide());
                        $transitions.onBefore({ to: 'thankYou' }, () => loadingService.show());
                        $transitions.onSuccess({ to: 'thankYou' }, () => loadingService.hide());
                    }
                },
                initLaunchDarkly: (tenantId, launchDarklyService) => launchDarklyService.init({ key: tenantId }),
                storeInfo: (setApiServiceTenant, storeInfoService) => storeInfoService.get(),
                returnUrl: storeInfo => storeInfo.canonicalHost,
                shipToLocations: (setApiServiceTenant, shipToLocationsService) => shipToLocationsService.get(),
                initGa: (storeInfo, gaService) => {
                    const gaId = storeInfo.googleAnalyticsId && storeInfo.googleAnalyticsId !== '' ?
                        storeInfo.googleAnalyticsId : null;
                    gaService.sendAppLoadedEvent(gaId);
                },
                favicon: ($rootScope, storeInfo) => {
                    $rootScope.faviconImageUrl = get(storeInfo, 'favicon.imageLink.fullUri', '');
                },
                pageTitle: ($rootScope, storeInfo) => {
                    $rootScope.storeTitle = get(storeInfo, 'seo.metaTitle', storeInfo.name);
                },
                updateLaunchDarkly: (initLaunchDarkly, launchDarklyService, storeInfo) => {
                    launchDarklyService.updateUser({
                        key: storeInfo.tenant,
                        email: storeInfo.email,
                        custom: {
                            planType: storeInfo.materialPlan,
                            storeName: storeInfo.name,
                            storeUrl: storeInfo.storeUrl
                        }
                    });
                },
                loadFacebookPixel: (storeInfo, scriptLoaderService) => {
                    const facebookTrackingPixelId = get(storeInfo, 'thirdPartyIntegrations.facebookTrackingPixelId');
                    if (facebookTrackingPixelId) {
                        scriptLoaderService.loadFacebookPixel(facebookTrackingPixelId);
                    }
                },
                loadGTM: (storeInfo, scriptLoaderService) => {
                    const googleTagManagerId = get(storeInfo, 'thirdPartyIntegrations.googleTagManagerId');
                    if (googleTagManagerId) {
                        scriptLoaderService.loadGTM(googleTagManagerId);
                    }
                },
                setLoadingServiceData: (storeInfo, loadingService) => loadingService.setStoreInfo(storeInfo)
            }
        }).state('checkout', {
            url: '/?bag&token&PayerID',
            component: 'componentRouter',
            parent: 'root',
            resolvePolicy: { when: 'EAGER' },
            resolve: {
                cart: (setApiServiceTenant, apiService, bag, $window, returnUrl, useNewCarts) => {
                    function redirectToStorefront() {
                        $window.location.href = returnUrl;
                    }

                    const getCart = useNewCarts ? apiService.getCart : apiService.getLegacyCart;

                    return getCart(bag.cartId).then((cart) => {
                        if (cart.items.length < 1) redirectToStorefront();
                        return cart;
                    }).catch(() => redirectToStorefront());
                },
                setCartId: (checkoutService, cart, useNewCarts) => {
                    const cartId = useNewCarts ? cart.cartId : cart.id;
                    checkoutService.setCartId(cartId);
                },
                availablePaymentMethods: (storeInfo, checkoutService) => checkoutService.getAvailablePaymentMethods(),
                sendPageViewEvent: (storeInfo, gaService, $window) => {
                    gaService.sendVirtualPageViewEvent($window.location.pathname, 'checkout', storeInfo.id);
                },
                shippingMethods: (setApiServiceTenant, apiService, useNewCarts, cart) => {
                    if (useNewCarts) {
                        const { shippingAddress } = getSavedCartInfo();
                        const { country, state } = shippingAddress || { country: 'US' };
                        return apiService.fetchShippingMethods({ cartId: cart.cartId, country, state });
                    }

                    return apiService.getShippingMethods();
                },
                useAddressVerification: storeInfo => storeInfo.enableAddressVerification
            }
        }).state('thankYou', {
            url: '/thankyou?bag&order&email&storeName',
            component: 'thankYou',
            parent: 'root',
            resolvePolicy: { when: 'EAGER' },
            resolve: {
                order: (setApiServiceTenant, apiService, $stateParams, tenantId, $window, returnUrl) => {
                    const hasOrderPlacedParams =
                        $stateParams.order &&
                        $stateParams.email &&
                        $stateParams.storeName &&
                        $stateParams.bag;

                    if (hasOrderPlacedParams) {
                        return apiService.getOrder(
                            decodeURIComponent($stateParams.order),
                            decodeURIComponent($stateParams.email),
                            tenantId
                        ).catch(() => {
                            $window.location.href = returnUrl;
                        });
                    }

                    $window.location.href = returnUrl;
                },
                sendPageViewEvent: (storeInfo, gaService, $location) => {
                    gaService.sendVirtualPageViewEvent($location.path(), 'thankYou', storeInfo.id);
                }
            }
        });
    })
    .config(($mdGestureProvider) => {
        $mdGestureProvider.skipClickHijack(); // https://github.com/angular/material/issues/10572
    })
    .name;



// WEBPACK FOOTER //
// ./src/app/app.js